'use client';

import {
  CSSProperties,
  ReactElement,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import useEmblaCarousel, { UseEmblaCarouselType } from 'embla-carousel-react';
import { cn } from '@/shared/utils/cn';
import ArrowLeft from '@/shared/components/primitives/svg/arrow-left';
import ArrowRight from '@/shared/components/primitives/svg/arrow-right';
import { Image } from '@/shared/components/image';
import { YouTubeIcon } from '@/shared/components/primitives/svg/youtube-icon';
import { useTranslations } from 'next-intl';

type EmblaApi = UseEmblaCarouselType[1];

type MediaItem = {
  kind: 'image' | 'youtube';
  url: string;
  thumbnailUrl: string;
  altText: string;
};

export interface ImageViewerProps {
  className?: string;
  defaultUrl?: string;
  items: string[];
  bigAspect?: 'square' | 'video' | 'auto';
  previewClassName?: string;
  maxPreviewWidthPx?: number;
  viewerMaxHeightVh?: number;
  viewerHeightPx?: number;
}

function getYoutubeId(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    const path = u.pathname;
    const params = u.searchParams;
    if (host === 'youtu.be') {
      const parts = path.split('/').filter(Boolean);
      return parts[0] ?? null;
    }
    if (host === 'youtube.com' || host.endsWith('youtube.com')) {
      if (path === '/watch') return params.get('v');
      if (path.startsWith('/shorts/')) return path.split('/')[2] ?? null;
      if (path.startsWith('/live/')) return path.split('/')[2] ?? null;
      if (path.startsWith('/embed/')) return path.split('/')[2] ?? null;
    }
    return null;
  } catch {
    return null;
  }
}

function isYoutubeUrl(url: string): boolean {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtube.com' || host === 'youtu.be' || host === 'm.youtube.com') return true;
    if (host.endsWith('youtube.com')) return true;
    return false;
  } catch {
    return false;
  }
}

function toYoutubeEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    const path = u.pathname;
    const params = u.searchParams;

    const build = (id: string, start?: number): string => {
      const base = `https://www.youtube.com/embed/${id}`;
      if (start && start > 0) return `${base}?start=${start}`;
      return base;
    };

    const parseStart = (): number | undefined => {
      const t = params.get('t') ?? params.get('start');
      if (!t) return undefined;
      const sec = parseInt(t, 10);
      return Number.isFinite(sec) && sec > 0 ? sec : undefined;
    };

    const start = parseStart();

    if (host === 'youtu.be') {
      const parts = path.split('/').filter(Boolean);
      const id = parts[0];
      return id ? build(id, start) : null;
    }

    if (host === 'youtube.com' || host.endsWith('youtube.com')) {
      if (path === '/watch') {
        const id = params.get('v');
        return id ? build(id, start) : null;
      }
      if (path.startsWith('/shorts/')) {
        const id = path.split('/')[2];
        return id ? build(id, start) : null;
      }
      if (path.startsWith('/live/')) {
        const id = path.split('/')[2];
        return id ? build(id, start) : null;
      }
      if (path.startsWith('/embed/')) return url;
    }
    return null;
  } catch {
    return null;
  }
}

function buildMediaItems(
  urls: readonly string[],
  t: (key: string) => string,
): readonly MediaItem[] {
  return urls.map((url, index) => {
    if (isYoutubeUrl(url)) {
      const id = getYoutubeId(url);
      const thumbnailUrl = id
        ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg`
        : `data:image/svg+xml;utf8,${encodeURIComponent(
            '<svg xmlns="http://www.w3.org/2000/svg" width="68" height="68"></svg>',
          )}`;
      return {
        kind: 'youtube',
        url,
        thumbnailUrl,
        altText: `${t('youtubeVideo')} ${index + 1}`,
      } as const;
    }
    return {
      kind: 'image',
      url,
      thumbnailUrl: url,
      altText: `${t('image')} ${index + 1}`,
    } as const;
  });
}

export const MediaViewer = ({
  className,
  defaultUrl,
  items,
  previewClassName,
  maxPreviewWidthPx,
  viewerMaxHeightVh,
  viewerHeightPx,
}: ImageViewerProps): ReactElement => {
  const t = useTranslations('GamePage');
  const id = useId();
  const mediaItems = useMemo(() => buildMediaItems(items, t), [items, t]);

  const matchIndex = useMemo(() => {
    if (!defaultUrl) return -1;
    return mediaItems.findIndex((m) => m.url === defaultUrl);
  }, [defaultUrl, mediaItems]);

  const initialIndex = useMemo(() => {
    if (mediaItems.length === 0) return 0;
    return matchIndex >= 0 ? matchIndex : 0;
  }, [mediaItems.length, matchIndex]);

  const [selectedIndex, setSelectedIndex] = useState<number>(initialIndex);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedIndex(initialIndex);
  }, [initialIndex]);

  const [viewportRef, emblaApi] = useEmblaCarousel({
    axis: 'x',
    loop: false,
    align: 'center',
    containScroll: 'trimSnaps',
    skipSnaps: false,
    dragFree: false,
  });

  const [canPrev, setCanPrev] = useState<boolean>(false);
  const [canNext, setCanNext] = useState<boolean>(false);
  const [allFit, setAllFit] = useState<boolean>(false);

  const overrideItem: MediaItem | null = useMemo(() => {
    if (!defaultUrl) return null;
    if (matchIndex >= 0) return null;
    const built = buildMediaItems([defaultUrl], t);
    return built[0] ?? null;
  }, [defaultUrl, matchIndex, t]);

  const [showOverride, setShowOverride] = useState<boolean>(!!overrideItem);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShowOverride(!!overrideItem);
  }, [overrideItem]);

  const onSelect = useCallback((api: EmblaApi) => {
    if (!api) return;
    setCanPrev(api.canScrollPrev());
    setCanNext(api.canScrollNext());
    try {
      const root = api.rootNode() as HTMLElement | null;
      const container = api.containerNode() as HTMLElement | null;
      if (root && container) {
        const fits = container.scrollWidth <= root.clientWidth + 1;
        setAllFit(fits);
      } else {
        setAllFit(false);
      }
    } catch {
      setAllFit(false);
    }
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    onSelect(emblaApi);
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('resize', onSelect);
    emblaApi.on('scroll', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
      emblaApi.off('resize', onSelect);
      emblaApi.off('scroll', onSelect);
    };
  }, [emblaApi, onSelect]);

  const handlePrev = useCallback(() => {
    setShowOverride(false);
    emblaApi?.scrollPrev();
  }, [emblaApi]);
  const handleNext = useCallback(() => {
    setShowOverride(false);
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const selected =
    showOverride && overrideItem ? overrideItem : (mediaItems[selectedIndex] ?? mediaItems[0]);

  const rootStyle: CSSProperties | undefined =
    viewerHeightPx != null
      ? { height: `${viewerHeightPx}px` }
      : viewerMaxHeightVh != null
        ? { height: `${viewerMaxHeightVh}vh` }
        : undefined;

  const mergedRootStyle: CSSProperties | undefined = rootStyle;
  const hasExplicitRootHeight: boolean = !!mergedRootStyle;

  const showArrows: boolean = !allFit;

  return (
    <div
      className={cn('flex h-full w-full flex-col overflow-hidden px-4', className)}
      style={mergedRootStyle}
    >
      <div
        className={cn(
          'relative mx-auto flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden',
          !hasExplicitRootHeight && 'aspect-video',
          previewClassName,
        )}
        style={{ maxWidth: maxPreviewWidthPx }}
      >
        {mediaItems.length === 0 ? (
          <div className='flex h-full w-full items-center justify-center text-sm text-gray-400'>
            {t('noMedia')}
          </div>
        ) : selected?.kind === 'image' ? (
          <Image
            alt={selected.altText}
            src={selected.url}
            fill
            className='object-contain object-center'
            sizes={`(max-width: 768px) 100vw, ${maxPreviewWidthPx}px`}
          />
        ) : (
          <iframe
            title={selected?.altText}
            src={toYoutubeEmbedUrl(selected.url) ?? selected.url}
            className='h-full w-full'
            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
            allowFullScreen
          />
        )}
      </div>
      <div className='mt-4 flex w-full shrink-0 items-center justify-center gap-2'>
        {showArrows && (
          <button
            type='button'
            aria-label={t('previous')}
            onClick={handlePrev}
            disabled={!canPrev}
            className={cn(!canPrev && 'pointer-events-none opacity-40')}
          >
            <ArrowLeft />
          </button>
        )}
        <div
          className='mx-auto w-full overflow-hidden'
          ref={viewportRef}
          style={{ maxWidth: maxPreviewWidthPx }}
        >
          <div className={cn('flex', allFit && 'justify-center')}>
            {mediaItems.map((m, i) => (
              <button
                key={`${id}-thumb-${i}`}
                type='button'
                aria-label={`Show media ${i + 1}`}
                onClick={() => {
                  setShowOverride(false);
                  setSelectedIndex(i);
                }}
                className={cn(
                  'relative mx-1 shrink-0 rounded-xs border border-transparent',
                  selectedIndex === i ? 'border-border' : '',
                )}
              >
                <div className='relative h-[68px] w-[68px]'>
                  <Image
                    alt={m.altText}
                    src={m.thumbnailUrl}
                    fill
                    className='object-contain object-center'
                    sizes={`${68}px`}
                  />
                  {m.kind === 'youtube' && (
                    <div className='absolute inset-0 flex items-center justify-center'>
                      <YouTubeIcon
                        width={24}
                        height={24}
                        className='bg-background-overlay-panel w-11'
                      />
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
        {showArrows && (
          <button
            type='button'
            aria-label={t('next')}
            onClick={handleNext}
            disabled={!canNext}
            className={cn(!canNext && 'pointer-events-none opacity-40')}
          >
            <ArrowRight />
          </button>
        )}
      </div>
    </div>
  );
};
